<template>
  <PuzzleListView
    :items="sightEntity.sights"
    :more-button="sightEntity.hasNext"
    :more-button-text="t('seeAll')"
    :more-button-link="allSightsLink"
    :loading="loading"
    :number-of-skeleton-cards="5"
    @load-more="showAllSights"
    @item-click="goToSight"
  >
    <template #default="{ item, position }">
      <PuzzleListArticleCard
        :item="{ ...item, link: `/journal/articles/${item.slug}/` }"
        :position="position"
      />
    </template>

    <template #skeleton>
      <PuzzleListArticleCardSkeleton :loading="skeletonLoading" />
    </template>
  </PuzzleListView>
</template>

<script lang="ts" setup>
  import { useAppRouter } from '@experience/core-app-link-lib';
  import { useI18n } from '@experience/i18n';
  import { storeToRefs } from 'pinia';
  import { computed } from 'vue';

  import { trackEvent } from '@/services/analytics';
  import { ru } from '@/pages/cityArticles/features/sightsList/i18n/ru';
  import PuzzleListView from '@/features/PuzzleListView.vue';
  import { useSightEntity } from '@/entities/sight';
  import type { ArticleInList } from '@/shared/models/articleInList';
  import { PuzzleListArticleCard, PuzzleListArticleCardSkeleton } from '@/shared/ui/puzzleListArticleCard';
  import { tryAwait } from '@/shared/utils/tryAwait';

  interface Props {
    /**
     * Ссылка для открытия страницы со списком всех достопримечательностей.
     */
    allSightsLink: string;

    /**
     * Идентификатор города.
     */
    subjectGeoId: number;

    /**
     * Название города.
     */
    subjectGeoName: string;

    /**
     * Флаг загрузки сторонних данных.
     *
     * Данный флаг нужен для того, чтобы не отсылать события аналитики до того
     * как загрузятся все данные, отправляемые в событиях.
     */
    generalPageDataLoading: boolean;
  }

  const props = defineProps<Props>();

  const { t } = useI18n({ inheritLocale: true, messages: { ru } });

  const { routerProxy } = useAppRouter();

  const sightEntity = useSightEntity();
  const { loading: sightLoading, loadingFailed } = storeToRefs(sightEntity);

  const loading = computed(() => sightLoading.value || props.generalPageDataLoading);
  const skeletonLoading = computed(() => loading.value && !loadingFailed.value);

  const showAllSights = async () => {
    await tryAwait(() =>
      trackEvent('city-articles-info:section:show-all.click', {
        subject_geo_id: props.subjectGeoId,
        subject_geo_name: props.subjectGeoName,
        subject_geo_type: 'city',
        section_type: 'sight',
      }),
    );

    await routerProxy.push(props.allSightsLink);
  };

  const goToSight = async (sight: ArticleInList) => {
    await tryAwait(() =>
      trackEvent('city-articles-info:section:snippet.click', {
        subject_geo_id: props.subjectGeoId,
        subject_geo_name: props.subjectGeoName,
        subject_geo_type: 'city',
        section_type: 'sight',
        snippet_id: sight.id,
      }),
    );

    await routerProxy.push(`/journal/articles/${sight.slug}/`);
  };
</script>
